import React, {
	useState,
	useEffect,
	useLayoutEffect,
	useRef,
	useCallback,
	useMemo,
} from "react";
import { useRoomContext } from "@livekit/components-react";
import PresenterCameraCard from "./PresenterCameraCard";
import PresentationAnnotationsOverlay from "./PresentationAnnotationsOverlay";
import { usePresentationAnnotationsReceive } from "./usePresentationAnnotationsReceive";
import type { Annotation } from "./presentationAnnotationTypes";

const MAX_UPSCALE = 2;

export interface PublishedPresentationData {
	id: string;
	name: string;
	type: string;
	pages: string[];
	totalPages: number;
	currentPage: number;
	currentZoom?: { scale: number; x: number; y: number };
	publisherIdentity?: string;
}

interface DocPresentationProps {
	presentation: PublishedPresentationData;
}

const DocPresentation: React.FC<DocPresentationProps> = ({ presentation }) => {
	const room = useRoomContext();
	const { pages, currentPage, currentZoom, name, publisherIdentity, id } =
		presentation;
	const totalPages = presentation.totalPages || pages?.length || 0;
	const currentImageUrl = pages?.[currentPage - 1] || "";
	const zoom = currentZoom || { scale: 1, x: 0, y: 0 };

	const contentRef = useRef<HTMLDivElement | null>(null);
	const imageWrapRef = useRef<HTMLDivElement | null>(null);
	const imgRef = useRef<HTMLImageElement | null>(null);
	const imageSizeCacheRef = useRef<Map<string, { w: number; h: number }>>(
		new Map()
	);
	const [naturalSize, setNaturalSize] = useState({ w: 0, h: 0 });
	const [containerSize, setContainerSize] = useState({ w: 0, h: 0 });
	const [suppressTransformTransition, setSuppressTransformTransition] =
		useState(false);

	const { currentPageAnnotations } = usePresentationAnnotationsReceive(
		room,
		id,
		currentPage
	);

	useEffect(() => {
		const el = contentRef.current;
		if (!el) return;
		const update = () => {
			const rect = el.getBoundingClientRect();
			setContainerSize({ w: rect.width, h: rect.height });
		};
		update();
		if (typeof ResizeObserver !== "undefined") {
			const ro = new ResizeObserver(update);
			ro.observe(el);
			return () => ro.disconnect();
		}
		window.addEventListener("resize", update);
		return () => window.removeEventListener("resize", update);
	}, []);

	const displayedSize = useMemo(() => {
		const { w: nw, h: nh } = naturalSize;
		const { w: cw, h: ch } = containerSize;
		if (!nw || !nh || !cw || !ch) return { w: 0, h: 0 };
		const containScale = Math.min(cw / nw, ch / nh, MAX_UPSCALE);
		return { w: nw * containScale, h: nh * containScale };
	}, [naturalSize, containerSize]);

	const shouldSplit = useMemo(() => {
		const { w: nw, h: nh } = naturalSize;
		const { w: cw, h: ch } = containerSize;
		if (!nw || !nh || !cw || !ch) return false;
		return nh / nw > 1.4 && cw / ch > 1.2;
	}, [naturalSize, containerSize]);

	const splitPanelSize = useMemo(() => {
		if (!shouldSplit) return null;
		const { w: nw, h: nh } = naturalSize;
		const { w: cw, h: ch } = containerSize;
		if (!nw || !nh || !cw || !ch) return null;
		const gap = 12;
		const availW = (cw - gap) / 2;
		const halfH = nh / 2;
		const panelScale = Math.min(availW / nw, ch / halfH, MAX_UPSCALE);
		const pw = nw * panelScale;
		const ph = halfH * panelScale;
		const fullImgH = nh * panelScale;
		return { pw, ph, fullImgH };
	}, [shouldSplit, naturalSize, containerSize]);

	const splitAnnotationSets = useMemo(() => {
		if (!shouldSplit || !splitPanelSize) return null;

		const getYExtent = (a: Annotation): [number, number] => {
			switch (a.kind) {
				case "rect":
					return [a.y, a.y + a.h];
				case "arrow":
					return [Math.min(a.y1, a.y2), Math.max(a.y1, a.y2)];
				case "text":
					return [a.y, a.y + (a.size ?? 0)];
				case "pen": {
					let min = Infinity, max = -Infinity;
					for (let i = 1; i < a.points.length; i += 2) {
						if (a.points[i] < min) min = a.points[i];
						if (a.points[i] > max) max = a.points[i];
					}
					return [min, max];
				}
				default:
					return [0, 0];
			}
		};

		const remapToPanel = (a: Annotation, offset: number): Annotation => {
			switch (a.kind) {
				case "rect":
					return { ...a, y: (a.y - offset) * 2, h: a.h * 2 };
				case "arrow":
					return {
						...a,
						y1: (a.y1 - offset) * 2,
						y2: (a.y2 - offset) * 2,
					};
				case "text":
					return { ...a, y: (a.y - offset) * 2, size: a.size * 2 };
				case "pen":
					return {
						...a,
						points: a.points.map((v, i) =>
							i % 2 === 1 ? (v - offset) * 2 : v
						),
					};
				default:
					return a;
			}
		};

		const top: Annotation[] = [];
		const bottom: Annotation[] = [];
		for (const a of currentPageAnnotations) {
			const [yMin, yMax] = getYExtent(a);
			if (yMax <= 0.5) top.push(remapToPanel(a, 0));
			else if (yMin >= 0.5) bottom.push(remapToPanel(a, 0.5));
		}
		return { top, bottom };
	}, [shouldSplit, splitPanelSize, currentPageAnnotations]);

	const applyNaturalSizeForUrl = useCallback((url: string, w: number, h: number) => {
		if (!url || w <= 0 || h <= 0) return;
		imageSizeCacheRef.current.set(url, { w, h });
		setNaturalSize({ w, h });
	}, []);

	const resolveNaturalSize = useCallback(
		(url: string) => {
			if (!url) return;
			const cached = imageSizeCacheRef.current.get(url);
			if (cached) {
				setNaturalSize(cached);
				return;
			}
			const img = imgRef.current;
			if (img?.complete && img.naturalWidth > 0) {
				applyNaturalSizeForUrl(url, img.naturalWidth, img.naturalHeight);
			}
		},
		[applyNaturalSizeForUrl]
	);

	useLayoutEffect(() => {
		if (!currentImageUrl) return;
		setSuppressTransformTransition(true);
		resolveNaturalSize(currentImageUrl);
		const frame = requestAnimationFrame(() => {
			setSuppressTransformTransition(false);
		});
		return () => cancelAnimationFrame(frame);
	}, [currentImageUrl, resolveNaturalSize]);

	useEffect(() => {
		setSuppressTransformTransition(true);
		const frame = requestAnimationFrame(() => {
			setSuppressTransformTransition(false);
		});
		return () => cancelAnimationFrame(frame);
	}, [currentPage]);

	const isImageLayoutReady =
		displayedSize.w > 0 &&
		displayedSize.h > 0 &&
		naturalSize.w > 0 &&
		naturalSize.h > 0;

	const handleImgLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
		const img = e.currentTarget;
		applyNaturalSizeForUrl(
			currentImageUrl,
			img.naturalWidth,
			img.naturalHeight
		);
	};

	if (!pages || pages.length === 0) {
		return (
			<div className="docPresentation">
				<div className="docPresentationLoading">Loading presentation...</div>
			</div>
		);
	}

	return (
		<div className="docPresentation">
			<div className="docPresentationContent" ref={contentRef}>
				{currentImageUrl ? (
					shouldSplit && splitPanelSize && splitAnnotationSets ? (
						<div
							className="docPresentationStage"
							style={{
								transform: `translate(${zoom.x}px, ${zoom.y}px) scale(${zoom.scale})`,
								transition: suppressTransformTransition
									? "none"
									: "transform 0.2s ease",
							}}
						>
							<div className="docSplitContainer">
								{(["top", "bottom"] as const).map((half, idx) => (
									<div
										key={half}
										className="docSplitPanel"
										style={{
											width: splitPanelSize.pw,
											height: splitPanelSize.ph,
										}}
									>
										<span className="docSplitPanelLabel">
											{idx + 1}
										</span>
										<img
											ref={idx === 0 ? imgRef : undefined}
											key={currentImageUrl + half}
											src={currentImageUrl}
											alt={`${name} - Page ${currentPage} Part ${idx + 1}`}
											className="docSplitPanelImg"
											draggable={false}
											onLoad={idx === 0 ? handleImgLoad : undefined}
											style={{
												width: splitPanelSize.pw,
												height: splitPanelSize.fullImgH,
												marginTop:
													half === "bottom"
														? -splitPanelSize.ph
														: 0,
											}}
										/>
										<PresentationAnnotationsOverlay
											annotations={
												half === "top"
													? splitAnnotationSets.top
													: splitAnnotationSets.bottom
											}
											displayedWidth={splitPanelSize.pw}
											displayedHeight={splitPanelSize.ph}
										/>
									</div>
								))}
							</div>
						</div>
					) : (
						<div
							className="docPresentationStage"
							style={{
								transform: `translate(${zoom.x}px, ${zoom.y}px) scale(${zoom.scale})`,
								transition: suppressTransformTransition
									? "none"
									: "transform 0.2s ease",
							}}
						>
							<div
								ref={imageWrapRef}
								className={`docPresentationImageWrap${
									isImageLayoutReady ? "" : " docPresentationImageWrapPending"
								}`}
								style={{
									width: isImageLayoutReady ? displayedSize.w : undefined,
									height: isImageLayoutReady ? displayedSize.h : undefined,
								}}
							>
								<img
									ref={imgRef}
									key={currentImageUrl}
									src={currentImageUrl}
									alt={`${name} - Page ${currentPage}`}
									className="docPresentationImageEl"
									draggable={false}
									onLoad={handleImgLoad}
								/>
								<PresentationAnnotationsOverlay
									annotations={currentPageAnnotations}
									displayedWidth={displayedSize.w}
									displayedHeight={displayedSize.h}
								/>
							</div>
						</div>
					)
				) : (
					<div className="docPresentationLoading">Page not available</div>
				)}
			</div>
			<div className="docPresentationFooter">
				<span className="docPresentationName">{name}</span>
				<span className="docPresentationPage">
					{currentPage} / {totalPages}
				</span>
			</div>
			<PresenterCameraCard publisherIdentity={publisherIdentity} />
		</div>
	);
};

export default DocPresentation;
